import React, { useEffect, useMemo, useState } from 'react';
import { Box, List, ListItem, ListItemSecondaryAction, Stack, TextField, Typography } from '@mui/material';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { useDispatch } from 'react-redux';
import { setActivityDrawer } from '@store/appStore';
import { buildCatalogOrigin } from '@api/ActivityLogs/CatalogActivity';
import * as Yup from 'yup';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import useConfirmationModal from '@hooks/useConfirmationModal';
import { ModalConfirmation } from '@components/Utilities/Modals/ModalConfirmation';
import ProductCategoriesService from '@api/ProductCategories/ProductCategoryCtrl';
import { ProductCategory } from '@api/ProductCategories/ProductCategory';

const saveProductCategorySchema = Yup.object({
    uid: Yup.string(),
    name: Yup.string().required('Nombre requerido')
});

const reorder = (list: ProductCategory[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

// Categorías de productos (colección `product-categories`, GLOBAL): las usan el menú viejo
// y los productos del catálogo 2.0 (`dishes`). Se mudó aquí desde el engranaje de /menu
// (decisión de Irving 2026-08-11); las Variables legacy siguen en aquel engranaje.
const ListProductCategories: React.FunctionComponent = () => {
    const productCategoriesService = useMemo(() => new ProductCategoriesService(), []);
    const [productCategories, setProductCategories] = useState<ProductCategory[]>([]);
    const [pendingDelete, setPendingDelete] = useState<ProductCategory | undefined>(undefined);
    const modalConfirmation = useConfirmationModal();

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors }
    } = useForm<ProductCategory>({
        resolver: yupResolver(saveProductCategorySchema)
    });

    const getData = async () => {
        showLoader();
        try {
            const data = await productCategoriesService.getProductCategories();
            setProductCategories(data);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onDragEnd = async (result: any) => {
        if (!result.destination) return;
        const updatedCategories = reorder(productCategories, result.source.index, result.destination.index);
        const updatedWithIndexes = updatedCategories.map((category, index) => ({
            ...category,
            priority: index
        }));
        setProductCategories(updatedWithIndexes);
        await productCategoriesService.updateProductCategoriesOrder(updatedWithIndexes);
    };

    const onSaveProductCategory: SubmitHandler<ProductCategory> = async (values) => {
        showLoader();
        try {
            values.priority = productCategories.length;
            await productCategoriesService.saveProductCategory(values);
            showAlertMessage('Categoría de producto guardada exitosamente');
            await getData();
            reset({ name: '' });
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    const dispatch = useDispatch();

    const onOpenActivity = (category: ProductCategory) => {
        if (!category.id) return;
        dispatch(
            setActivityDrawer({
                isOpen: true,
                section: 'product-category',
                sectionId: category.id,
                derived: buildCatalogOrigin(category, 'creó la categoría', 'Categoría creada')
            })
        );
    };

    const onDeleteClick = (category: ProductCategory) => {
        setPendingDelete(category);
        modalConfirmation.setModalData(
            'Eliminar categoría',
            `¿Eliminar la categoría "${category.name}"? Los productos que la usan quedarán "Sin categoría".`
        );
    };

    const onDeleteConfirm = async () => {
        if (!pendingDelete?.id) return;
        showLoader();
        try {
            await productCategoriesService.deleteProductCategory(pendingDelete.id);
            const updatedCategories = productCategories.filter((category) => category.id !== pendingDelete.id);
            const updatedWithPriorities = updatedCategories.map((category, index) => ({
                ...category,
                priority: index
            }));
            setProductCategories(updatedWithPriorities);
            await productCategoriesService.updateProductCategoriesOrder(updatedWithPriorities);
            showAlertMessage('Categoría de producto eliminada exitosamente');
            await getData();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        void getData();
    }, []);

    return (
        <Stack height="100%" spacing={2}>
            <Typography variant="body2" color="text.secondary">
                Categorías en las que se divide el menú (arrastra para reordenar — el orden se refleja en POS y en
                línea). Las usan tanto el menú actual como los productos del catálogo 2.0.
            </Typography>
            <Box maxWidth={560}>
                <form onSubmit={handleSubmit(onSaveProductCategory)}>
                    <Controller
                        name="name"
                        control={control}
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                            <TextField
                                fullWidth
                                label="Nueva categoría — escribe y presiona Enter"
                                variant="outlined"
                                value={value}
                                onChange={onChange}
                                error={!!errors.name}
                                helperText={errors.name && errors.name.message}
                            />
                        )}
                    />
                </form>
            </Box>
            <Box flex={1} minHeight={0} overflow="auto" maxWidth={560}>
                <Typography variant="body2" color="text.secondary" mb={1}>
                    Sin categoría (por defecto)
                </Typography>
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="product-categories-droppable">
                        {(provided) => (
                            <List ref={provided.innerRef} {...provided.droppableProps}>
                                {productCategories.map((category, index) => (
                                    <Draggable key={category.id} draggableId={category.id || ''} index={index}>
                                        {(provided, snapshot) => (
                                            <ListItem
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                sx={{
                                                    backgroundColor: snapshot.isDragging ? '#F0F3F6' : 'transparent',
                                                    border: '1px solid #E3E8EE',
                                                    borderRadius: 2,
                                                    mb: 1
                                                }}
                                            >
                                                <DragIndicatorIcon sx={{ color: '#B7C2CC', mr: 1 }} fontSize="small" />
                                                <ListItemText primary={category.name} />
                                                <ListItemSecondaryAction>
                                                    {/*
                                                        Ésta es la lista donde se
                                                        REORDENA el menú. El
                                                        2026-09-10 salió desordenado
                                                        en producción y no hubo forma
                                                        de saber quién lo movió: cada
                                                        categoría movida deja ahora su
                                                        renglón, y aquí está la puerta
                                                        para verlo.
                                                    */}
                                                    <IconButton
                                                        onClick={() => onOpenActivity(category)}
                                                        title="Actividad"
                                                    >
                                                        <PriorityHighIcon />
                                                    </IconButton>
                                                    <IconButton onClick={() => onDeleteClick(category)}>
                                                        <DeleteOutlineIcon />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </List>
                        )}
                    </Droppable>
                </DragDropContext>
            </Box>
            <ModalConfirmation modalConfirmation={modalConfirmation} onConfirmation={onDeleteConfirm} />
        </Stack>
    );
};

export default ListProductCategories;
